import type { RouteObject } from 'react-router-dom'
import { useRoutes } from 'react-router-dom'

import Home from '@/pages/Home'
import Empleos from '@/pages/Empleos'
import Empresas from '@/pages/Empresas'
import Capacitaciones from '@/pages/Capacitaciones'
import ComoFunciona from '@/pages/ComoFunciona'
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import NotFound from '@/pages/NotFound'

import EmpresaDashboard from '@/pages/dashboard/EmpresaDashboard'
import ColaboradorDashboard from '@/pages/dashboard/ColaboradorDashboard'
import AdminDashboard from '@/pages/dashboard/AdminDashboard'
import EmpresaPerfil from '@/pages/empresa/EmpresaPerfil'
import EmpresaVacantes from '@/pages/empresa/EmpresaVacantes'
import EmpresaPostulantes from '@/pages/empresa/EmpresaPostulantes'
import EmpresaColaboradores from '@/pages/empresa/EmpresaColaboradores'
import EmpresaEvaluaciones from '@/pages/empresa/EmpresaEvaluaciones'
import ColaboradorPerfil from '@/pages/colaborador/ColaboradorPerfil'
import ColaboradorPostulaciones from '@/pages/colaborador/ColaboradorPostulaciones'
import ColaboradorVinculaciones from '@/pages/colaborador/ColaboradorVinculaciones'
import ColaboradorEvaluaciones from '@/pages/colaborador/ColaboradorEvaluaciones'
import ColaboradorHistorial from '@/pages/colaborador/ColaboradorHistorial'
import ColaboradorReportes from '@/pages/colaborador/ColaboradorReportes'
import EmpresaReportes from '@/pages/empresa/EmpresaReportes'
import EmpresaCasos from '@/pages/empresa/EmpresaCasos'
import EmpresaNotificaciones from '@/pages/empresa/EmpresaNotificaciones'
import AdminCasos from '@/pages/admin/AdminCasos'
import AdminEmpresas from '@/pages/admin/AdminEmpresas'
import AdminReportes from '@/pages/admin/AdminReportes'
import AdminColaboradores from '@/pages/admin/AdminColaboradores'
import AdminVacantes from '@/pages/admin/AdminVacantes'
import AdminCapacitaciones from '@/pages/admin/AdminCapacitaciones'
import AdminNotificaciones from '@/pages/admin/AdminNotificaciones'
import ColaboradorCapacitaciones from '@/pages/colaborador/ColaboradorCapacitaciones'
import ColaboradorNotificaciones from '@/pages/colaborador/ColaboradorNotificaciones'
import ColaboradorCertificados from '@/pages/colaborador/ColaboradorCertificados'
import AdminEvaluaciones from '@/pages/admin/AdminEvaluaciones'
import AdminConfiguracion from '@/pages/admin/AdminConfiguracion'
import EmpresaReputacion from '@/pages/empresa/EmpresaReputacion'
import EmpresaPlantillas from '@/pages/empresa/EmpresaPlantillas'
import EmpresaEntrevistas from '@/pages/empresa/EmpresaEntrevistas'
import EmpresaBancoTalento from '@/pages/empresa/EmpresaBancoTalento'
import AdminGestionTalento from '@/pages/admin/AdminGestionTalento'
import ColaboradorEmpleos from '@/pages/colaborador/ColaboradorEmpleos'
import ColaboradorCursos from '@/pages/colaborador/ColaboradorCursos'
import AdminCursos from '@/pages/admin/AdminCursos'
import EmpresaAspectos from '@/pages/empresa/EmpresaAspectos'
import AdminAspectosEvaluacion from '@/pages/admin/AdminAspectosEvaluacion'

import ProtectedRoute from '@/components/ProtectedRoute'
import AdminRoute from '@/components/AdminRoute'
import ModulePlaceholder from '@/components/ModulePlaceholder'

// Helpers para placeholders internos de módulos en construcción
const empresaModule = (title: string, description: string) => (
  <ProtectedRoute requireType="empresa">
    <ModulePlaceholder title={title} description={description} backTo="/empresa/dashboard" />
  </ProtectedRoute>
)

const colaboradorModule = (title: string, description: string) => (
  <ProtectedRoute requireType="colaborador">
    <ModulePlaceholder title={title} description={description} backTo="/colaborador/dashboard" />
  </ProtectedRoute>
)

const adminModule = (title: string, description: string) => (
  <AdminRoute>
    <ModulePlaceholder title={title} description={description} backTo="/admin/dashboard" backLabel="Volver al panel del sistema" />
  </AdminRoute>
)

// Used in @/prerender.tsx
export const routes: RouteObject[] = [
  // Públicas
  { path: '/', element: <Home /> },
  { path: '/empleos', element: <Empleos /> },
  { path: '/empresas', element: <Empresas /> },
  { path: '/capacitaciones', element: <Capacitaciones /> },
  { path: '/como-funciona', element: <ComoFunciona /> },
  { path: '/login', element: <Login /> },
  { path: '/registro', element: <Register /> },

  // Empresa (sesión Lumi + tipo empresa)
  {
    path: '/empresa/dashboard',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/perfil',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaPerfil />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/vacantes',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaVacantes />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/postulantes',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaPostulantes />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/colaboradores',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaColaboradores />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/evaluaciones',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaEvaluaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/reportes',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaReportes />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/casos',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaCasos />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/reputacion',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaReputacion />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/aspectos',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaAspectos />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/notificaciones',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaNotificaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/plantillas',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaPlantillas />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/entrevistas',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaEntrevistas />
      </ProtectedRoute>
    ),
  },
  {
    path: '/empresa/banco-talento',
    element: (
      <ProtectedRoute requireType="empresa">
        <EmpresaBancoTalento />
      </ProtectedRoute>
    ),
  },

  // Colaborador (sesión Lumi + tipo colaborador)
  {
    path: '/colaborador/dashboard',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/perfil',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorPerfil />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/empleos',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorEmpleos />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/postulaciones',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorPostulaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/vinculaciones',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorVinculaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/evaluaciones',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorEvaluaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/historial',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorHistorial />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/capacitaciones',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorCapacitaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/certificados',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorCertificados />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/reportes',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorReportes />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/notificaciones',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorNotificaciones />
      </ProtectedRoute>
    ),
  },
  {
    path: '/colaborador/cursos',
    element: (
      <ProtectedRoute requireType="colaborador">
        <ColaboradorCursos />
      </ProtectedRoute>
    ),
  },

  // Administrador del Sistema
  {
    path: '/admin/dashboard',
    element: (
      <AdminRoute>
        <AdminDashboard />
      </AdminRoute>
    ),
  },
  { path: '/admin/empresas', element: <AdminRoute><AdminEmpresas /></AdminRoute> },
  { path: '/admin/colaboradores', element: <AdminRoute><AdminColaboradores /></AdminRoute> },
  { path: '/admin/vacantes', element: <AdminRoute><AdminVacantes /></AdminRoute> },
  { path: '/admin/evaluaciones', element: <AdminRoute><AdminEvaluaciones /></AdminRoute> },
  {
    path: '/admin/casos',
    element: (
      <AdminRoute>
        <AdminCasos />
      </AdminRoute>
    ),
  },
  { path: '/admin/capacitaciones', element: <AdminRoute><AdminCapacitaciones /></AdminRoute> },
  { path: '/admin/notificaciones', element: <AdminRoute><AdminNotificaciones /></AdminRoute> },
  { path: '/admin/reportes', element: <AdminRoute><AdminReportes /></AdminRoute> },
  { path: '/admin/configuracion', element: <AdminRoute><AdminConfiguracion /></AdminRoute> },
  { path: '/admin/gestion-talento', element: <AdminRoute><AdminGestionTalento /></AdminRoute> },
  { path: '/admin/cursos', element: <AdminRoute><AdminCursos /></AdminRoute> },
  { path: '/admin/aspectos-evaluacion', element: <AdminRoute><AdminAspectosEvaluacion /></AdminRoute> },

  // 404
  { path: '*', element: <NotFound /> },
]

function App() {
  return useRoutes(routes)
}

export default App
